import React, { useState } from 'react';
import { CreateNotificationDto } from '../api/notifications';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface CreateNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateNotificationDto) => Promise<void>;
}

const CreateNotificationModal: React.FC<CreateNotificationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateNotificationDto>({
    type: 'general',
    title: '',
    message: '',
    priority: 'MEDIUM',
    recipientId: '',
    entityType: 'user',
    entityId: '',
    metadata: {},
    scheduledFor: '',
    expiresAt: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean up the data before submitting
      const submitData: CreateNotificationDto = {
        ...formData,
        recipientId: formData.recipientId || undefined,
        entityId: formData.entityId || undefined,
        scheduledFor: formData.scheduledFor || undefined,
        expiresAt: formData.expiresAt || undefined,
      };

      await onSubmit(submitData);
      handleReset();
    } catch (error) {
      console.error('Error creating notification:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      type: 'general',
      title: '',
      message: '',
      priority: 'MEDIUM',
      recipientId: '',
      entityType: 'user',
      entityId: '',
      metadata: {},
      scheduledFor: '',
      expiresAt: '',
    });
    onClose();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Create New Notification" onClose={onClose} />
      <form onSubmit={handleSubmit}>
        <BrandModalBody>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">                    {/* Type */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Type *
                      </label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      >
                        <option key="general-1753461297287" value="general">General</option>
                        <option key="breeding-1753461297287" value="breeding">Breeding</option>
                        <option key="health-1753461297287" value="health">Health</option>
                        <option key="task-1753461297287" value="task">Task</option>
                        <option key="supply_alert-1753461297287" value="supply_alert">Supply Alert</option>
                        <option key="equipment_alert-1753461297287" value="equipment_alert">Equipment Alert</option>
                        <option key="disease_alert-1753461297287" value="disease_alert">Disease Alert</option>
                        <option key="financial_alert-1753461297287" value="financial_alert">Financial Alert</option>
                        <option key="customer_notification-1753461297287" value="customer_notification">Customer Notification</option>
                        <option key="document_expiry-1753461297287" value="document_expiry">Document Expiry</option>
                        <option key="system-1753461297287" value="system">System</option>
                        <option key="alert-1753461297287" value="alert">Alert</option>
                      </select>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Priority *
                      </label>
                      <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      >
                        <option key="low-1753461297287" value="low">Low</option>
                        <option key="medium-1753461297287" value="medium">Medium</option>
                        <option key="high-1753461297287" value="high">High</option>
                        <option key="urgent-1753461297287" value="urgent">Urgent</option>
                        <option key="critical-1753461297287" value="critical">Critical</option>
                      </select>
                    </div>

                    {/* Title */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700">
                        Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        placeholder="Enter notification title"
                      />
                    </div>

                    {/* Message */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        placeholder="Enter notification message"
                      />
                    </div>

                    {/* Entity Type */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Entity Type
                      </label>
                      <select
                        name="entityType"
                        value={formData.entityType || ''}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      >
                        <option key="empty-option-1753461297287" value="">Select entity type...</option>
                        <option key="breeding_pair-1753461297287" value="breeding_pair">Breeding Pair</option>
                        <option key="bird-1753461297287" value="bird">Bird</option>
                        <option key="species-1753461297287" value="species">Species</option>
                        <option key="clutch-1753461297287" value="clutch">Clutch</option>
                        <option key="egg-1753461297287" value="egg">Egg</option>
                        <option key="cage-1753461297287" value="cage">Cage</option>
                        <option key="task-1753461297287" value="task">Task</option>
                        <option key="user-1753461297287" value="user">User</option>
                        <option key="feed_stock-1753461297287" value="feed_stock">Feed Stock</option>
                        <option key="supply-1753461297287" value="supply">Supply</option>
                        <option key="equipment-1753461297287" value="equipment">Equipment</option>
                        <option key="health_test-1753461297287" value="health_test">Health Test</option>
                        <option key="disease_record-1753461297287" value="disease_record">Disease Record</option>
                        <option key="document-1753461297287" value="document">Document</option>
                        <option key="transaction-1753461297287" value="transaction">Transaction</option>
                      </select>
                    </div>

                    {/* Entity ID */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Entity ID
                      </label>
                      <input
                        type="text"
                        name="entityId"
                        value={formData.entityId}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        placeholder="Enter entity ID"
                      />
                    </div>

                    {/* Scheduled For */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Scheduled For
                      </label>
                      <input
                        type="datetime-local"
                        name="scheduledFor"
                        value={formData.scheduledFor}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      />
                    </div>

                    {/* Expires At */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700">
                        Expires At
                      </label>
                      <input
                        type="datetime-local"
                        name="expiresAt"
                        value={formData.expiresAt}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      />
                    </div>
                  </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Notification'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default CreateNotificationModal; 